import { context, trace } from '@opentelemetry/api';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isNil, noop } from 'lodash';
import { useCurrentValueGetter, useIsHeavyAccount } from 'hooks';
import { useTabVisibility } from '../../../hooks/useTabVisibility';
import { useAccount } from '../account-configurations';
import { APP_EVENT_TYPE } from '../app-events-aggregator';
import { useAppEventExtension } from '../app-events-aggregator/hooks/useAppEventExtension';
import { authentificationSelector, userSelector } from '../authentication';
import { throwCriticalError } from '../formData/utils/throwCriticalError';
import { useIsTracerPermitted } from './hooks/useIsTracerPermitted';
import { useTracerFilterConfig } from './hooks/useTracerFilterConfig';
import { startTracing, TRACER_NAME } from './utils/tracerConfiguration';

export const SpanAlreadyExistsError = new Error('Span with the same name already exists.');
const EMPTY_MAP = new Map();
const CHILD_EMPTY_MAP = new Map();
const DEFAULT_TRACER_CONTEXT_VALUE = {
    createRootSpan: noop,
    getRootSpan: noop,
    endRootSpan: noop,
    startSpan: noop,
    getSpan: noop,
    endSpan: noop,
    setSpanStatus: noop,
    stopMeasurement: noop,
};
export const TracerContext = createContext(DEFAULT_TRACER_CONTEXT_VALUE);

export const useTracerContext = () => useContext(TracerContext);

export const TracerProvider = ({ children }) => {
    const isTracingEnabled = useIsTracerPermitted();
    const isAuthenticated = useSelector(authentificationSelector);
    const tracerFilter = useTracerFilterConfig();
    const spanFiltersConfigGetter = useCurrentValueGetter(tracerFilter);

    const { accountName, type: accountType } = useAccount();
    const { user_id } = useSelector(userSelector);
    const extendEvent = useAppEventExtension();
    const isHeavyAccount = useIsHeavyAccount();

    const tracer = trace.getTracer(TRACER_NAME);
    const globalSpanRef = useRef(EMPTY_MAP);
    const childSpansRef = useRef(CHILD_EMPTY_MAP);

    const getRootSpan = useCallback(
        name => {
            if (!isNil(name)) {
                return globalSpanRef.current.get(name);
            }
        },
        [globalSpanRef]
    );

    const setEventAttributes = useCallback(
        async span => {
            const fullEvent = await extendEvent({ type: APP_EVENT_TYPE.PERFORMANCE_MEASUREMENT });
            span.setAttributes(fullEvent);
        },
        [extendEvent]
    );

    const getSpan = useCallback(
        name => {
            if (!isNil(name)) {
                return childSpansRef.current.get(name);
            }
        },
        [childSpansRef]
    );

    const endSpan = useCallback(name => {
        const span = childSpansRef.current.get(name);
        if (span) {
            span.end();
            childSpansRef.current.delete(name);
        }
    }, []);

    const endRootSpan = useCallback(
        name => {
            const rootSpan = globalSpanRef.current.get(name);
            if (rootSpan) {
                const rootSpanId = rootSpan.spanContext().spanId;
                childSpansRef.current.forEach((childSpan, childKey) => {
                    if (childSpan.parentSpanId === rootSpanId) {
                        childSpan.end();
                        childSpansRef.current.delete(childKey);
                    }
                });
                rootSpan.end();
                globalSpanRef.current.delete(name);
            }
        },
        [globalSpanRef]
    );

    const setSpanStatus = useCallback((span, status, message) => {
        if (span) {
            span.setStatus({ code: status, message });
        }
    }, []);

    const stopMeasurement = useCallback(() => {
        globalSpanRef.current?.forEach((_, name) => {
            const rootSpan = globalSpanRef.current.get(name);
            if (rootSpan) {
                const rootSpanId = rootSpan.spanContext().spanId;
                childSpansRef.current.forEach((childSpan, childKey) => {
                    if (childSpan.parentSpanId === rootSpanId) {
                        childSpansRef.current.delete(childKey);
                    }
                });
                globalSpanRef.current.delete(name);
            }
        });
    }, []);

    const isPageVisible = useTabVisibility({ onVisible: noop, onHidden: stopMeasurement });

    const createRootSpan = useCallback(
        (name, options) => {
            if (globalSpanRef.current.has(name)) {
                throwCriticalError(SpanAlreadyExistsError);
            }
            if (!isPageVisible) {
                return;
            }

            const span = tracer.startSpan(name, options);

            span.setAttributes({ UserId: user_id, accountType, isHeavyAccount });

            globalSpanRef.current.set(name, span);

            setEventAttributes(span).catch(error => {
                console.error('Failed to add events to span:', error);
            });

            return span;
        },
        [isPageVisible, tracer, user_id, accountType, isHeavyAccount, setEventAttributes]
    );

    const startSpan = useCallback(
        (name, parentSpan, attributes = {}) => {
            if (!parentSpan || !isPageVisible) {
                return;
            }
            const ctxWithParent = trace.setSpan(context.active(), parentSpan);

            context.with(ctxWithParent, () => {
                const childSpan = tracer.startSpan(name);
                childSpan.setAttributes(attributes);

                childSpansRef.current.set(name, childSpan);
            });
        },
        [isPageVisible, tracer]
    );

    useEffect(() => {
        globalSpanRef.current.clear();
        childSpansRef.current.clear();
        if (isAuthenticated) {
            startTracing(spanFiltersConfigGetter);
        }
    }, [isAuthenticated, spanFiltersConfigGetter]);

    useEffect(() => {
        globalSpanRef.current?.forEach((_, name) => {
            endRootSpan(name);
        });
    }, [accountName, endRootSpan]);

    const value = useMemo(
        () => ({
            createRootSpan,
            getRootSpan,
            endRootSpan,
            startSpan,
            getSpan,
            endSpan,
            childSpansRef,
            setSpanStatus,
            stopMeasurement,
        }),
        [createRootSpan, getRootSpan, endRootSpan, startSpan, getSpan, endSpan, setSpanStatus, stopMeasurement]
    );

    return (
        <TracerContext.Provider value={isTracingEnabled ? value : DEFAULT_TRACER_CONTEXT_VALUE}>
            {children}
        </TracerContext.Provider>
    );
};
